/**
 * Module 6C.3 — First-run CTA banners.
 *
 * Two dismissable cards on /dashboard:
 *   1. Invite teammate (owner-only) → routes to /settings, scrolls to roster.
 *   2. Connect a lead source (import_use holders) → routes to /import.
 *
 * Each card is independently dismissable; dismissing one doesn't hide the
 * other. When BOTH are dismissed, the parent wrapper unmounts entirely.
 * The card list is computed from the gates, so a PM (no team_manage,
 * no import_use) sees nothing — the component renders null.
 *
 * No new backend logic. Both CTAs are thin entry points to existing
 * flows (POST /api/team and the /import page).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { X, UserPlus, Database, ArrowRight } from "lucide-react";

import { useAuth } from "@/lib/auth";
import {
  dismissOnboarding,
  shouldShowInviteCta,
  shouldShowLeadSourceCta,
} from "@/lib/onboarding";


function CtaCard({ icon: Icon, title, body, ctaLabel, onCta, onDismiss, testId, accent = "ink" }) {
  // Spec §4: onboarding cards have a 3px left accent border, either ink (pine)
  // or gold. Variants are passed via the `accent` prop ("ink" | "gold").
  const accentColor = accent === "gold" ? "var(--ds-accent)" : "var(--ds-ink)";
  const tileBg      = accent === "gold" ? "var(--ds-accent-bg)" : "#E4EDDF";
  const tileInk     = accent === "gold" ? "var(--ds-accent)" : "var(--ds-ink)";
  return (
    <div
      className="p-5 flex items-start gap-4 relative"
      style={{
        background: "var(--ds-surface)",
        border: "1px solid var(--ds-surface-line)",
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 8,
        boxShadow: "0 1px 2px rgba(60,46,20,.04)",
      }}
      data-testid={testId}
    >
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3"
        style={{ color: "#B9B1A2", transition: "color .15s" }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#6E675B")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#B9B1A2")}
        data-testid={`${testId}-dismiss`}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
      <div
        className="shrink-0 flex items-center justify-center"
        style={{
          width: 44, height: 44,
          background: tileBg,
          color: tileInk,
          borderRadius: 12,
        }}
      >
        <Icon size={20} strokeWidth={2} />
      </div>
      <div className="flex-1 pr-6">
        <div className="text-sm font-semibold mb-1" style={{ color: "var(--ds-ink)" }}>
          {title}
        </div>
        <div className="text-xs mb-4 leading-relaxed" style={{ color: "var(--ds-text-2)" }}>{body}</div>
        <button
          onClick={onCta}
          className="inline-flex items-center gap-1.5"
          style={{
            background: accentColor,
            color: "#F4EFE4",
            padding: "10px 18px",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.13em",
            textTransform: "uppercase",
            borderRadius: 5,
            border: "none",
            cursor: "pointer",
            transition: "filter .15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(0.94)")}
          onMouseLeave={(e) => (e.currentTarget.style.filter = "none")}
          data-testid={`${testId}-cta`}
        >
          {ctaLabel} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}


export default function FirstRunCTAs({ state, onStateChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const showInvite = shouldShowInviteCta(state, user);
  const showLeadSource = shouldShowLeadSourceCta(state, user);

  if (!showInvite && !showLeadSource) return null;

  const handleDismiss = async (key) => {
    try {
      const next = await dismissOnboarding(key);
      onStateChange && onStateChange(next);
    } catch {}
  };

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 mb-8"
      style={{ gap: 20 }}
      data-testid="first-run-ctas"
    >
      {showInvite && (
        <CtaCard
          icon={UserPlus}
          title="Bring your team in"
          body="Invite your sales, PM, delivery or accounts lead — they'll sign in with Google."
          ctaLabel="Invite teammate"
          onCta={() => navigate("/settings")}
          onDismiss={() => handleDismiss("invite_cta")}
          testId="cta-invite-team"
          accent="ink"
        />
      )}
      {showLeadSource && (
        <CtaCard
          icon={Database}
          title="Connect your first lead source"
          body="Pull leads from Google Sheets, CSV, or WedMeGood — fresh leads land in your Pipeline automatically."
          ctaLabel="Set up imports"
          onCta={() => navigate("/import")}
          onDismiss={() => handleDismiss("lead_source_cta")}
          testId="cta-lead-source"
          accent="gold"
        />
      )}
    </div>
  );
}
